import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axiosInstance';
import apiConfig from '../../config/appConfig';

const UserForm = ({ open, onClose, initialValues, isUpdate, refreshUsers, setSnackbar }) => {
  const validationSchema = Yup.object({
    username: Yup.string().required('Username is required').min(4, 'Username must be at least 4 characters'),
    password: Yup.string()
      .when('isUpdate', {
        is: false,
        then: schema => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
        otherwise: schema => schema.min(6, 'Password must be at least 6 characters if you wish to change it'),
      }),
    role: Yup.string().required('Role is required').oneOf(['user', 'admin']),
    status: Yup.string().required('Status is required').oneOf(['active', 'inactive', 'suspended']),
  });

  const formik = useFormik({
    initialValues: {
      username: initialValues?.username || '',
      password: '',
      role: initialValues?.role || 'user',
      status: initialValues?.status || 'active',
      isUpdate,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const submissionValues = { ...values };
      if (isUpdate && !submissionValues.password) {
        delete submissionValues.password;
      }
      delete submissionValues.isUpdate;

      try {
        if (isUpdate) {
          // Edit User
          await axiosInstance.put(`${apiConfig.endpoints.users.base}/${initialValues.userId}`, submissionValues);
          setSnackbar({ open: true, message: "User updated successfully!", severity: "success" });
        } else {
          // Add User
          await axiosInstance.post(apiConfig.endpoints.users.base, submissionValues);
          setSnackbar({ open: true, message: "User added successfully!", severity: "success" });
        }
        refreshUsers();
        onClose();
      } catch (error) {
        console.error("Submit User Error:", error.response?.data?.message || error.message);
        const errorMessage = error.response?.data?.message || (isUpdate ? "Failed to update user" : "Failed to add user");
        setSnackbar({ open: true, message: errorMessage, severity: "error" });
      }
    },
    enableReinitialize: true,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isUpdate ? 'Edit User' : 'Add New User'}</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            id="username"
            name="username"
            label="Username"
            value={formik.values.username}
            onChange={formik.handleChange}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
          <TextField
            fullWidth
            margin="dense"
            id="password"
            name="password"
            label={isUpdate ? 'New Password (optional)' : 'Password'}
            type="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
          <FormControl fullWidth margin="dense" error={formik.touched.role && Boolean(formik.errors.role)}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
            <FormHelperText>{formik.touched.role && formik.errors.role}</FormHelperText>
          </FormControl>
          <FormControl fullWidth margin="dense" error={formik.touched.status && Boolean(formik.errors.status)}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={formik.values.status}
              onChange={formik.handleChange}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
            <FormHelperText>{formik.touched.status && formik.errors.status}</FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={() => { formik.resetForm(); onClose(); }}>Cancel</Button>
          <Button type="submit" color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;